import { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Trophy } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { calculateTennisStats } from '../../../utils/statCalculations';
import { getTennisInsights } from '../../../data/tennisInsights';
import { ServeTrendChart, type ServeTrendPoint } from './ServeTrendChart';
import { RallyBucketChart, type RallyBucketDatum } from './RallyBucketChart';
import { TennisStatTile, TennisStatSection, TennisStatCategorySections } from '../../games/tennis/stats/TennisStatTiles';
import type { SportStat } from '../../../types';

interface TennisStatsPanelProps {
  sportStats: SportStat[];
  teamId: string;
}

function sumByKey(stats: SportStat[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const s of stats) {
    totals[s.stat_key] = (totals[s.stat_key] ?? 0) + s.stat_value;
  }
  return totals;
}

const RALLY_BUCKET_ORDER: Array<{ key: string; label: string }> = [
  { key: 'Rally_1to3', label: '1-3 shots' },
  { key: 'Rally_4to6', label: '4-6 shots' },
  { key: 'Rally_7to9', label: '7-9 shots' },
  { key: 'Rally_10Plus', label: '10+ shots' },
];

/**
 * Tennis-specific player analytics (sc-339 / Tennis Phase 5). Reads the same
 * raw sport_stats rows PlayerCardDetail already loads — only fetches one
 * extra thing itself (game date/opponent for the serve trend chart, since
 * that lives on `games`, not `sport_stats`). Stat categories (Serve, Return &
 * Pressure, Shot Quality, Court Position) are the shared tile sections also
 * used on the match detail page and match summary — only Record & Trend and
 * Rally Profile (season-only: a trend chart, a season record line) are
 * specific to this season view.
 */
export function TennisStatsPanel({ sportStats, teamId }: TennisStatsPanelProps) {
  const [serveTrend, setServeTrend] = useState<ServeTrendPoint[]>([]);

  const totals = useMemo(() => sumByKey(sportStats), [sportStats]);
  const calc = useMemo(
    () => calculateTennisStats(Object.entries(totals).map(([stat_key, stat_value]) => ({ stat_key, stat_value }))),
    [totals]
  );
  const insights = useMemo(() => getTennisInsights(totals), [totals]);

  const rallyBuckets: RallyBucketDatum[] = useMemo(
    () => RALLY_BUCKET_ORDER.map(({ key, label }) => ({ label, played: totals[key] ?? 0, won: totals[`${key}Won`] ?? 0 })),
    [totals]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const byGame = new Map<string, SportStat[]>();
      for (const s of sportStats) {
        if (!s.game_id) continue;
        if (!byGame.has(s.game_id)) byGame.set(s.game_id, []);
        byGame.get(s.game_id)!.push(s);
      }
      const gameIds = [...byGame.keys()];
      if (gameIds.length === 0) {
        setServeTrend([]);
        return;
      }

      const { data: games, error } = await supabase
        .from('games')
        .select('id, date, opponent_name')
        .eq('team_id', teamId)
        .in('id', gameIds)
        .order('date', { ascending: true });
      if (cancelled || error || !games) return;

      const trend: ServeTrendPoint[] = games
        .map((g) => {
          const gameTotals = sumByKey(byGame.get(g.id) ?? []);
          const attempted = gameTotals['ServePts_1st'] ?? 0;
          const in1st = gameTotals['ServePts_1stIn'] ?? 0;
          if (attempted === 0) return null;
          return {
            game_id: g.id,
            date: g.date,
            opponent_name: g.opponent_name,
            first_serve_pct: Math.round((in1st / attempted) * 100),
          };
        })
        .filter((p): p is ServeTrendPoint => p !== null);

      if (!cancelled) setServeTrend(trend);
    })();
    return () => {
      cancelled = true;
    };
  }, [sportStats, teamId]);

  const hasAnyData = Object.keys(totals).length > 0;
  if (!hasAnyData) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center text-sm text-gray-500">
        No match stats yet — stats populate automatically after a scored match.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {insights.length > 0 && (
        <div className="space-y-2">
          {insights.map((insight) => (
            <div key={insight.id} className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Lightbulb size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm text-amber-900">{insight.message}</p>
                <p className="text-xs text-amber-700 mt-0.5">Suggested: {insight.practiceThemes.join(' · ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <TennisStatSection title="Record & Trend">
        <TennisStatTile label="Matches Won" value={String(totals.MatchWon ?? 0)} />
        <TennisStatTile label="Sets" value={`${totals.SetsWon ?? 0}-${totals.SetsLost ?? 0}`} />
        <TennisStatTile label="Games" value={`${totals.GamesWon ?? 0}-${totals.GamesLost ?? 0}`} />
        <TennisStatTile label="Tiebreaks" value={`${totals.TiebreaksWon ?? 0}/${totals.TiebreaksPlayed ?? 0}`} />
      </TennisStatSection>
      <ServeTrendChart trend={serveTrend} />

      <TennisStatCategorySections totals={totals} calc={calc} />

      <TennisStatSection title="Rally Profile">
        <div className="col-span-2 sm:col-span-3">
          <RallyBucketChart buckets={rallyBuckets} />
        </div>
      </TennisStatSection>

      {(totals.MatchWon ?? 0) > 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <Trophy size={12} />
          Season totals across every scored match.
        </p>
      )}
    </div>
  );
}

export default TennisStatsPanel;
